import { apiClient } from './api'

export interface Region {
  id: string
  name: string
  regionId: string
}

export interface District {
  id: string
  name: string
  districtId: string
  regionId: string
  region?: Region
}

export interface Director {
  id: string
  name: string
  directorId: string
  district?: District & {
    region: Region
  }
}

export interface Store {
  id: string
  name: string
  fullName: string
  storeId: string
  abbreviation: string
  regionId: string
  districtId: string
  managerEmail?: string
  assistantManagerEmail?: string
  region?: Region
  district?: District
  storeType: string
  compStatus?: string
  originalOpenDate?: string
  reloDate?: string
  compDate?: string
  matureNewDate?: string
  storeAge?: string
  ageSort?: number
}

export interface UserWithLocation {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  branchId?: string
  createdAt: string
  updatedAt: string
  store?: Store & {
    district: District & {
      region: Region
    }
  }
}

export interface LocationHierarchy {
  regions: (Region & {
    districts: (District & {
      stores: Store[]
    })[]
  })[]
}

export interface LocationStats {
  totalRegions: number
  totalDistricts: number
  totalDirectors: number
  totalStores: number
  usersWithStores: number
  storeManagers: number
}

export interface SyncResult {
  success: boolean
  message: string
  data?: {
    regionsProcessed: number
    districtsProcessed: number
    storesProcessed: number
    errors: string[]
  }
  errors?: string[]
}

export const locationService = {
  /**
   * Get all locations in hierarchical structure
   */
  async getLocationHierarchy(): Promise<LocationHierarchy> {
    const response = await apiClient.get('/locations/hierarchy')
    return response.data.data
  },

  /**
   * Get all regions
   */
  async getRegions(): Promise<Region[]> {
    const response = await apiClient.get('/locations/regions')
    console.log('Regions API response:', response.data)
    return response.data.regions || []
  },

  /**
   * Get all districts, optionally filtered by region
   */
  async getDistricts(regionId?: string): Promise<District[]> {
    const params = regionId ? { regionId } : {}
    const response = await apiClient.get('/locations/districts', { params })
    console.log('Districts API response:', response.data)
    return response.data.districts || []
  },

  /**
   * Get all directors
   */
  async getDirectors(): Promise<Director[]> {
    const response = await apiClient.get('/locations/directors')
    console.log('Directors API response:', response.data)
    return response.data.directors || []
  },

  /**
   * Get all stores, optionally filtered by region or district
   * Only returns retail stores (excludes warehouses, discount stores, etc.)
   */
  async getStores(filters?: { regionId?: string; districtId?: string }): Promise<Store[]> {
    const response = await apiClient.get('/locations/stores', { params: filters })
    console.log('Stores API response:', response.data)
    return response.data.stores || []
  },

  /**
   * Get store by store ID
   */
  async getStoreByStoreId(storeId: string): Promise<Store> {
    const response = await apiClient.get(`/locations/stores/${storeId}`)
    return response.data.store
  },

  /**
   * Get users with their location information
   */
  async getUsersWithLocations(filters?: {
    regionId?: string
    districtId?: string
    storeId?: string
  }): Promise<UserWithLocation[]> {
    const response = await apiClient.get('/locations/users', { params: filters })
    console.log('Users API response:', response.data)
    return response.data.users || []
  },

  /**
   * Get location statistics
   */
  async getLocationStats(): Promise<LocationStats> {
    const response = await apiClient.get('/locations/stats')
    console.log('Stats API response:', response.data)
    return response.data.stats
  },

  /**
   * Sync location data from PowerBI
   */
  async syncFromPowerBI(): Promise<SyncResult> {
    const response = await apiClient.post('/locations/sync-powerbi')
    return response.data
  }
}